import { useEffect, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import Layout from '../../components/Layout'
import { CHANGE_ITEMS_PAGE } from '../../store/types'
import PagnationComponent from '../../components/PagnationComponent'
import { Button, Container, Typography } from '@mui/material'
import { getAllItems } from '../../store/actions/itemAction'
import ItemInfos from '../../components/item/ItemInfos'
import { roleListId, roleType } from '../../config/roleList'
import { checkUserRole } from '../../common/checkUserRole'
import SearchItem from '../../components/item/SearchItem'
import request from '../../config/request'
import { getErrors } from '../../store/actions/errorActions'
import { controlLoading } from '../../store/actions/loadingActions'
import BasicModal from '../../components/modal/BasicModal'
import ItemUploadForm from '../../components/item/ItemUploadForm'
import ItemExcelAddForm from '../../components/item/ItemExcelAddForm'

const Items = () => {
    const dispatch = useDispatch()
    const { userToken } = useSelector((state) => state.auth)
    const navigate = useNavigate()
    const itemState = useSelector((state) => state.item)
    const { items, pages, page, count, keyword } = itemState
    const { userInfo } = useSelector((state) => state.auth)
    const { roleList } = useSelector((state) => state.user)
    const [open, setOpen] = useState(false)
    const [addOpen, setAddOpen] = useState(false)

    const pageNumber = page || 1
    useEffect(() => {
        dispatch(getAllItems(keyword, pageNumber))
    }, [dispatch, keyword, pageNumber])

    const changePage = useCallback(
        (page) => {
            dispatch({ type: CHANGE_ITEMS_PAGE, payload: page })
        },
        [dispatch]
    )

    const downloadStock = useCallback(async () => {
        try {
            dispatch(controlLoading(true))
            const res = await request.get('item/itemstock', {
                headers: { Authorization: `Bearer ${userToken}` },
            })
            const stockData = res.data.data

            const worksheet = XLSX.utils.json_to_sheet(stockData)

            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, worksheet, 'StockData')

            const excelBuffer = XLSX.write(workbook, {
                bookType: 'xlsx',
                type: 'array',
            })

            const blob = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })

            saveAs(blob, `ItemStock${Date.now()}.xlsx`)
            dispatch(controlLoading(false))
        } catch (e) {
            dispatch(getErrors('Download stock error'))
            dispatch(controlLoading(false))
        }
    }, [dispatch, userToken])

    const downloadItemDetails = useCallback(async () => {
        try {
            dispatch(controlLoading(true))
            const res = await request.get('item/listalldetails', {
                headers: { Authorization: `Bearer ${userToken}` },
            })
            const allItems = res.data.data

            const formattedData = allItems.map((item) => {
                return {
                    ID: item.item_id,
                    'Item SKU': item.item_itemSKU,
                    'Item Name': item.item_itemName,
                    Width: item.item_packageLong,
                    Height: item.item_packageWidth,
                    Depth: item.item_packageHeight,
                    Volume: item.item_volume,
                    Weight: item.item_weight,
                    'Weight Unit': item.item_weightUnit,
                    MSRP: item.item_MSRPinCAD,
                    Description: item.item_description,
                    'Picture Link': item.item_picLink,
                    'Alert Stock': item.item_alertStock,
                    'Auto Order Qty': item.item_autoOrderQty,
                    'Pcs Per Box': item.item_pcsPerBox,
                    Online: item.item_online,
                    'Item Style ID': item.itemstyleId,
                }
            })

            const header = [
                'ID',
                'Item SKU',
                'Item Name',
                'Width',
                'Height',
                'Depth',
                'Volume',
                'Weight',
                'Weight Unit',
                'MSRP',
                'Description',
                'Picture Link',
                'Alert Stock',
                'Auto Order Qty',
                'Pcs Per Box',
                'Online',
                'Item Style ID',
            ]

            const worksheet = XLSX.utils.json_to_sheet(formattedData, {
                header,
            })

            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, worksheet, 'itemsDetails')

            const excelBuffer = XLSX.write(workbook, {
                bookType: 'xlsx',
                type: 'array',
            })

            const blob = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })

            saveAs(blob, `ItemsDetails${Date.now()}.xlsx`)
            dispatch(controlLoading(false))
        } catch (e) {
            dispatch(getErrors('Download items details error'))
            dispatch(controlLoading(false))
        }
    }, [dispatch, userToken])

    const checkAllItemsStock = useCallback(async () => {
        if (window.confirm('This process may take several minutes')) {
            try {
                dispatch(controlLoading(true))
                await request.get('item/checkallitemstock', {
                    headers: { Authorization: `Bearer ${userToken}` },
                })
                dispatch(controlLoading(false))
            } catch (error) {
                console.log('error ==>', error)
                dispatch(controlLoading(false))
                dispatch(getErrors(error?.msg))
            }
        }
    }, [dispatch, userToken])
    return (
        <Layout>
            <Container>
                <BasicModal open={open} setOpen={setOpen}>
                    <ItemUploadForm setOpen={setOpen} />
                </BasicModal>
                <BasicModal open={addOpen} setOpen={setAddOpen}>
                    <ItemExcelAddForm setOpen={setAddOpen} />
                </BasicModal>
                <Button
                    color="info"
                    variant="outlined"
                    style={{ marginTop: '10px' }}
                    onClick={() =>
                        checkUserRole(
                            userInfo,
                            roleList,
                            roleListId.ITEM,
                            roleType.CREATE
                        )
                            ? navigate(`/additem`)
                            : alert('Not Permit User')
                    }
                >
                    Add New Item
                </Button>
                <Button
                    color="success"
                    variant="outlined"
                    onClick={() => downloadStock()}
                    style={{ marginLeft: '10px', marginTop: '10px' }}
                >
                    Download Stock
                </Button>
                <Button
                    color="secondary"
                    variant="outlined"
                    onClick={() =>
                        checkUserRole(
                            userInfo,
                            roleList,
                            roleListId.ITEM,
                            roleType.READ
                        )
                            ? navigate(`/allitemsdata`)
                            : alert('Not Permit User')
                    }
                    style={{ marginLeft: '10px', marginTop: '10px' }}
                >
                    All Items Analytics
                </Button>
                <Button
                    color="error"
                    variant="outlined"
                    onClick={() =>
                        checkUserRole(
                            userInfo,
                            roleList,
                            roleListId.ITEM,
                            roleType.APPROVAL
                        )
                            ? checkAllItemsStock()
                            : alert('Not Permit User')
                    }
                    style={{ marginLeft: '10px', marginTop: '10px' }}
                >
                    Check Items Stock
                </Button>
                <Button
                    color="primary"
                    variant="outlined"
                    onClick={() => downloadItemDetails()}
                    style={{ marginLeft: '10px', marginTop: '10px' }}
                >
                    Download All Items
                </Button>
                {checkUserRole(
                    userInfo,
                    roleList,
                    roleListId.ITEM,
                    roleType.EDIT
                ) && (
                    <Button
                        color="secondary"
                        variant="outlined"
                        onClick={() => setOpen(true)}
                        style={{ marginLeft: '10px', marginTop: '10px' }}
                    >
                        Upload Items Excel
                    </Button>
                )}
                {checkUserRole(
                    userInfo,
                    roleList,
                    roleListId.ITEM,
                    roleType.CREATE
                ) && (
                    <Button
                        color="info"
                        variant="outlined"
                        onClick={() => setAddOpen(true)}
                        style={{ marginLeft: '10px', marginTop: '10px' }}
                    >
                        Add Items Excel
                    </Button>
                )}
                <SearchItem displayKeyword={keyword} />
                <Typography
                    variant="body2"
                    gutterBottom
                    style={{ paddingBottom: '10px', paddingTop: '10px' }}
                >
                    Count: {count}
                </Typography>
                {items && <ItemInfos items={items} />}
                <PagnationComponent
                    page={pageNumber}
                    pages={pages}
                    func={changePage}
                />
            </Container>
        </Layout>
    )
}

export default Items
